/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Sticky from 'react-stickynode';
import isEmpty from 'lodash/isEmpty';
import { Typography, LoadingButton, Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import {
  Footer,
  LinkButton,
  DisclaimerText
} from './styles';
import { saveConfig } from '../../actions/designActions';
import { scheduleMeasureUrlSelector } from '../../selectors/scheduleMeasureUrl';
import {
  installationThresholdMetSelector,
  totalProjectPrice,
  getRegionalCarpetInstallPrice,
  totalRegionalInstallPrice,
  selectSelectedCarpetChoice
} from '../../selectors/pricing';
import { roomMeasuresWithStairs } from '../../selectors/roomMeasure';
import ProjectSummarySelection from '../projectSummarySelection/index';
import SummaryLineItems from '../SummaryLineItems/index';
import MeasureItemsList from './measureItemsList';
import { InstallationThreshold } from '../InstallationThreshold';
import { estimateDisclaimerText, installDisclaimerText } from './constants';
import { trackNavigationLinkTracking } from '../../utils/analytics/trackEvents';
import { selectSelectedSkuData } from '../../selectors/skuDataSelectors/index';
import { findTaggedValue } from '../../utils/tags/findTags';

function setChoiceTextFromTags({ tags, value }) {
  const collection = findTaggedValue(tags, 'collection');
  const brand = findTaggedValue(tags, 'brand');
  const carpettype = findTaggedValue(tags, 'carpettype');
  return `${collection} - ${brand} - ${value} ${carpettype} Installed Carpet`;
}

const ProjectSummary = ({ bottomBoundary, canContinue, isOpen, isSkeleton }) => {
  const [showEstimateDisclaimer, setShowEstimateDisclamier] = useState(false);
  const [selectedCarpetChoiceText, setSelectedCarpetChoiceText] = useState('');
  const [loadPirateCart, setLoadPirateCart] = useState(false);

  if (isSkeleton) {
    const defaultImageUrl = 'https://images.thdstatic.com/productImages/66a37596-2188-4333-ab62-43cd45e123a6/svn/black-satin-home-decorators-collection-texture-carpet-hdd7980504-64_100.jpg';
    return (
      <div className="project-summary-container">
        <Sticky bottomBoundary={bottomBoundary}>
          <div className="summary-card">
            <h2 className="sui-h2-bold sui-line-clamp-unset sui-text-primary">Your Carpet Project</h2>
            <ProjectSummarySelection isSkeleton defaultImageUrl={defaultImageUrl} />
            <SummaryLineItems isSkeleton />
            <div className="project-summary-footer">
              <LoadingButton
                variant="primary"
                disabled={!canContinue}
                loading={loadPirateCart}
                fullWidth
                onClick={() => {}}
              >
                Save and Continue
              </LoadingButton>
              <p>
                <a href="#" rel="noreferrer" target="_blank" onClick={() => {}}>
                  Schedule an in-home carpet measure
                </a>
              &nbsp;to get a quote for your project
              </p>
            </div>
          </div>
        </Sticky>
      </div>
    );
  }

  const dispatch = useDispatch();

  const { samUrl } = useSelector(scheduleMeasureUrlSelector);
  const measuresList = useSelector(roomMeasuresWithStairs);
  const carpetSelection = useSelector(selectSelectedCarpetChoice);
  const hasRegionalInstallPrice = useSelector(getRegionalCarpetInstallPrice);
  const regionalInstallPrice = useSelector(totalRegionalInstallPrice);
  const hasFreeInstallation = useSelector(installationThresholdMetSelector);
  const total = useSelector(totalProjectPrice);
  const inEditFlow = useSelector((state) => state.design.configurationSaved);
  const selectedSku = useSelector(selectSelectedSkuData);
  const [mediaUrl, setMediaUrl] = useState(null);

  /* we set the selection image based on if we are in super sku experience or not.
  if superSku we pass the carpetSelection to SummarySelection component with the
  replaced img from the product graphQL instead of the mediaImg on the choice. */
  useEffect(() => {
    const isSuperSkuExperience = !isEmpty(selectedSku);
    const hasGraphData = selectedSku?.productLabel;
    if (isSuperSkuExperience && hasGraphData) {
      setSelectedCarpetChoiceText(selectedSku.productLabel);
      setMediaUrl(selectedSku.primaryImageUrl.replace('<SIZE>', '100'));
    } else if (!isSuperSkuExperience && !isEmpty(carpetSelection)) {
      setSelectedCarpetChoiceText(setChoiceTextFromTags(carpetSelection));
    }
  }, [selectedSku, carpetSelection?.id]);

  const trackSamClick = () => {
    trackNavigationLinkTracking('project summary', 'Schedule a Measure Only');
  };

  const handleSaveConfiguration = () => {
    setLoadPirateCart(true);
    dispatch(saveConfig());
  };

  return isOpen ? (
    <div className="project-summary-container">
      <Sticky bottomBoundary={bottomBoundary}>
        <div className="summary-card">
          <Typography variant="h2" weight="bold">Your Carpet Project</Typography>
          <ProjectSummarySelection
            selectedCarpetChoiceText={selectedCarpetChoiceText}
            carpetSelection={mediaUrl ? { ...carpetSelection, mediaUrl } : carpetSelection}
          />
          <MeasureItemsList measuresList={measuresList} hasRegionalInstallPrice={hasRegionalInstallPrice} regionalInstallPrice={regionalInstallPrice} />
          <SummaryLineItems />
          {!hasRegionalInstallPrice && (
            !hasFreeInstallation ? <InstallationThreshold /> : <DisclaimerText>{installDisclaimerText}</DisclaimerText>
          )}
          {(total > 0) && (
            <>
              <LinkButton onClick={() => setShowEstimateDisclamier(!showEstimateDisclaimer)}>
                This is an estimate
              </LinkButton>{ (showEstimateDisclaimer) && (<DisclaimerText>{estimateDisclaimerText}</DisclaimerText>) }
            </>
          )}
          <div className="project-summary-footer">
            <LoadingButton
              variant="primary"
              disabled={!canContinue}
              loading={loadPirateCart}
              fullWidth
              onClick={() => handleSaveConfiguration()}
            >
              {inEditFlow ? 'Update My Carpet' : 'Save and Continue'}
            </LoadingButton>
            <p>
              <a href={samUrl} rel="noreferrer" target="_blank" onClick={trackSamClick}>
                Schedule an in-home carpet measure
              </a>
              &nbsp;to get a quote for your project
            </p>
          </div>
        </div>
      </Sticky>
    </div>
  ) : null;

};

ProjectSummary.propTypes = {
  bottomBoundary: PropTypes.string,
  canContinue: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool,
  isSkeleton: PropTypes.bool,
};

ProjectSummary.defaultProps = {
  bottomBoundary: '#root',
  isOpen: true,
  isSkeleton: false,
};

export default ProjectSummary;
